import React from "react"
import {
  Box, Radio, RadioGroup, Stack, Text,
} from "@chakra-ui/react"
import { WhatsAppTemplate } from "sharedTypes"
import {
  TemplateFilters,
  MESSAGE_TYPE_OPTIONS,
  LANGUAGE_OPTIONS,
} from "utilities/whatsappTemplates"

type Props = {
  channel: "sms" | "whatsapp"
  filters: TemplateFilters
  onFilterChange: (filters: TemplateFilters) => void
  templates: WhatsAppTemplate[]
}

const CATEGORY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "utility", label: "Utility" },
  { value: "marketing", label: "Marketing" },
]

const TemplateFilterSidebar = ({
  channel, filters, onFilterChange, templates,
}: Props) => {
  const updateFilter = (key: keyof TemplateFilters, value: string | boolean) => {
    onFilterChange({ ...filters, [key]: value })
  }

  const categoryValue = (() => {
    if (filters.showUtility && filters.showMarketing) return "all"
    if (filters.showUtility) return "utility"
    if (filters.showMarketing) return "marketing"

    return "all"
  })()

  const channelTemplates = templates.filter(
    (t) => t.channels.includes(channel),
  )
  const categoryFilteredTemplates = channelTemplates.filter((t) => {
    if (categoryValue === "all") return true

    return t.category === categoryValue
  })

  const availableMessageTypes = new Set(categoryFilteredTemplates.map((t) => t.messageType))
  const availableLanguages = new Set(categoryFilteredTemplates.map((t) => t.language))

  const visibleMessageTypes = MESSAGE_TYPE_OPTIONS.filter(
    (o) => o.value === "all" || availableMessageTypes.has(o.value),
  )
  const visibleLanguages = LANGUAGE_OPTIONS.filter(
    (o) => o.value === "all" || availableLanguages.has(o.value),
  )

  const handleCategoryChange = (value: string) => {
    const newFilters = {
      ...filters,
      showUtility: value === "all" || value === "utility",
      showMarketing: value === "all" || value === "marketing",
    }

    const newCategoryTemplates = channelTemplates.filter((t) => {
      if (value === "all") return true

      return t.category === value
    })
    const newAvailableTypes = new Set(newCategoryTemplates.map((t) => t.messageType))
    if (filters.messageType !== "all" && !newAvailableTypes.has(filters.messageType)) {
      newFilters.messageType = "all"
    }

    onFilterChange(newFilters)
  }

  return (
    <Box px={6} py={4} h="100%">
      <Stack spacing={6}>
        {channel === "whatsapp" && (
          <FilterSection title="Type">
            <RadioGroup value={categoryValue} onChange={handleCategoryChange}>
              <Stack spacing={2}>
                {CATEGORY_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          </FilterSection>
        )}

        <FilterSection title="Purpose">
          <RadioGroup
            value={filters.messageType}
            onChange={(val) => updateFilter("messageType", val)}
          >
            <Stack spacing={2}>
              {visibleMessageTypes.map((option) => (
                <Radio key={option.value} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Stack>
          </RadioGroup>
        </FilterSection>

        {visibleLanguages.length > 2 && (
          <FilterSection title="Language">
            <RadioGroup
              value={filters.language}
              onChange={(val) => updateFilter("language", val)}
            >
              <Stack spacing={2}>
                {visibleLanguages.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>
          </FilterSection>
        )}
      </Stack>
    </Box>
  )
}

type FilterSectionProps = {
  title: string
  children: React.ReactNode
}

const FilterSection = ({ title, children }: FilterSectionProps) => (
  <Box>
    <Text fontWeight="semibold" mb={2}>
      {title}
    </Text>
    {children}
  </Box>
)

export default TemplateFilterSidebar
